import React from 'react';
import { Building2, ArrowRight, ShieldCheck, Briefcase, Activity, Ban as Bank, ShoppingBag, Truck, Globe, Factory, Server, Microscope } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <Bank className="w-8 h-8 text-yellow-400" />,
      title: "Financial Services",
      description: "Custom solutions for banking, investment, and insurance firms with a focus on security and compliance.",
      features: [
        "Secure transaction processing",
        "Risk management systems",
        "Regulatory compliance",
        "Real-time analytics"
      ]
    },
    {
      icon: <Activity className="w-8 h-8 text-yellow-400" />,
      title: "Education",
      description: "Comprehensive solutions for educational institutions, from K-12 to higher education.",
      features: [
        "Learning management systems",
        "Student information systems",
        "Online assessment platforms",
        "Administrative tools"
      ]
    },
    {
      icon: <Factory className="w-8 h-8 text-yellow-400" />,
      title: "Manufacturing",
      description: "Streamline operations and optimize production with smart manufacturing solutions.",
      features: [
        "Production planning",
        "Quality control",
        "Supply chain management",
        "Predictive maintenance"
      ]
    },
    {
      icon: <ShoppingBag className="w-8 h-8 text-yellow-400" />,
      title: "Retail & E-commerce",
      description: "End-to-end solutions for modern retail operations and e-commerce platforms.",
      features: [
        "Inventory management",
        "POS systems",
        "Customer analytics",
        "Omnichannel retail"
      ]
    },
    {
      icon: <Truck className="w-8 h-8 text-yellow-400" />,
      title: "Logistics & Transportation",
      description: "Optimize routes, track shipments, and manage fleet operations efficiently.",
      features: [
        "Fleet management",
        "Route optimization",
        "Real-time tracking",
        "Warehouse management"
      ]
    },
    {
      icon: <Globe className="w-8 h-8 text-yellow-400" />,
      title: "Technology",
      description: "Cutting-edge solutions for technology companies and startups.",
      features: [
        "Cloud infrastructure",
        "API development",
        "DevOps automation",
        "Scalable architecture"
      ]
    },
    {
      icon: <Server className="w-8 h-8 text-yellow-400" />,
      title: "Telecommunications",
      description: "Advanced solutions for telecom providers and communication networks.",
      features: [
        "Network management",
        "Billing systems",
        "Service provisioning",
        "Customer portals"
      ]
    },
    {
      icon: <Microscope className="w-8 h-8 text-yellow-400" />,
      title: "Research & Development",
      description: "Support scientific research and development with specialized software solutions.",
      features: [
        "Data analysis",
        "Lab management",
        "Research collaboration",
        "Documentation systems"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Industries We Serve</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Delivering innovative software solutions across diverse industries,
              tailored to meet specific sector challenges and requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-6">{industry.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{industry.title}</h3>
                <p className="text-gray-600 mb-6">{industry.description}</p>
                <ul className="space-y-3">
                  {industry.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-500">
                      <ShieldCheck className="w-4 h-4 text-yellow-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Building2 className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Why Choose Us</h2>
            <p className="text-lg text-gray-600">
              Our deep industry expertise and technical excellence ensure solutions
              that drive real business value.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Industry Expertise",
                description: "Deep understanding of industry-specific challenges and requirements"
              },
              {
                title: "Custom Solutions",
                description: "Tailored software solutions designed for your unique business needs"
              },
              {
                title: "Proven Track Record",
                description: "Successfully delivered projects across multiple industries"
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how we can help transform your business with custom software solutions.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;